import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { PenTool, RotateCcw, Check, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { useIntl } from "react-intl";

export function SignatureModal({
  isOpen,
  onClose,
  onSignatureComplete,
  appointmentId,
  clientName,
}) {
  const intl = useIntl();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [customerName, setCustomerName] = useState(clientName);
  const [customerEmail, setCustomerEmail] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Set up canvas
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
      }
    }
  }, [isOpen]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    setIsDrawing(true);
    setHasSignature(true);

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ("touches" in e) {
      e.preventDefault();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = () => {
    if (!hasSignature) {
      toast({
        title: intl.formatMessage({
          id: "Appointment.SignatureRequired",
          defaultMessage: "Signature Required",
        }),
        description: intl.formatMessage({
          id: "Appointment.SignatureRequiredDesc",
          defaultMessage: "Please provide a signature before completing",
        }),
        variant: "error",
      });
      return;
    }

    if (!customerName.trim()) {
      toast({
        title: intl.formatMessage({
          id: "Appointment.NameRequired",
          defaultMessage: "Name Required",
        }),
        description: intl.formatMessage({
          id: "Appointment.NameRequiredDesc",
          defaultMessage: "Please enter the customer name",
        }),
        variant: "error",
      });
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const signatureData = canvas.toDataURL("image/png");

    onSignatureComplete({
      signature: signatureData,
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim() || undefined,
      timestamp: new Date().toISOString(),
    });

    toast({
      title: intl.formatMessage({
        id: "Appointment.SignatureCollected",
        defaultMessage: "Signature Collected",
      }),
      description: intl.formatMessage({
        id: "Appointment.SignatureCollectedDesc",
        defaultMessage: "Customer signature has been saved successfully",
      }),
      variant: "error",
    });

    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="signature-modal-description"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="h-5 w-5 text-blue-600" />
            Customer Signature Required
          </DialogTitle>
          <p
            id="signature-modal-description"
            className="text-sm text-gray-600 dark:text-gray-400"
          >
            Please collect the customer's signature to complete this
            appointment.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Email (Optional)</Label>
              <Input
                id="customerEmail"
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="customer@example.com"
              />
            </div>
          </div>

          {/* Signature Pad */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Digital Signature *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSignature}
                    className="flex items-center gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Clear
                  </Button>
                </div>

                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg w-full h-48 cursor-crosshair bg-white dark:bg-gray-50"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="text-gray-400 dark:text-gray-500 text-center">
                        <PenTool className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">
                          Sign here with your finger or mouse
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              <Check className="h-4 w-4" />
              Complete Appointment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SignatureModal;
